import type { Fighter, Scene } from './types';

export interface RingColor { rgb: string; fill: string }
export const RING_GREEN: RingColor = { rgb: '57,208,90', fill: '#39d05a' }; // your fighter
export const RING_RED: RingColor = { rgb: '255,71,71', fill: '#ff4747' }; // selected enemy
export const RING_SMOKE: RingColor = { rgb: '150,170,185', fill: '#9aaab9' }; // selected teammate (smoke target)

// Pulsing ground-ring + the three orbiting "selection-lock" chevrons — one highlight,
// always drawn together. Sits UNDER the sprite so it reads as a floor decal and never
// covers the body. A dark contour under a solid colored stroke keeps it crisp on any
// background. Same style for you (green) and target (red/smoke). On a dead target the
// ring stays (locked), but the spinning chevrons drop off.
export function drawRing(sc: Scene, f: Fighter, now: number, c: RingColor) {
  const { ctx } = sc;
  const pulse = 0.5 + 0.5 * Math.sin(now * 4);
  const rx = 72 + pulse * 7, ry = 26 + pulse * 3;
  ctx.save();
  // dark contour first — makes the color pop instead of blending into the floor
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.lineWidth = 11;
  ctx.beginPath();
  ctx.ellipse(f.x, f.y - 2, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();
  // glow = one wide translucent stroke, NOT shadowBlur: a per-frame gaussian on two
  // pulsing rings was a real per-frame rasterization cost on mobile
  ctx.strokeStyle = `rgba(${c.rgb},0.35)`;
  ctx.lineWidth = 14;
  ctx.beginPath();
  ctx.ellipse(f.x, f.y - 2, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();
  // solid colored ring on top (opaque, no alpha fade)
  ctx.strokeStyle = `rgb(${c.rgb})`;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.ellipse(f.x, f.y - 2, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  if (f.dead) return; // corpse keeps the ring, but the live spinning chevrons stop
  drawMarkers(sc, f, now, c);
}

// Three chevrons orbiting the ground ring — reads as a live "selection lock" that
// keeps spinning while the fighter is highlighted. Rides the same ellipse as
// drawRing (a touch outside the stroke) and is drawn UNDER the sprite, so the
// arrows chase around the feet without ever covering the body.
function drawMarkers(sc: Scene, f: Fighter, now: number, c: RingColor) {
  const { ctx } = sc;
  const cx = f.x, cy = f.y - 2;
  const rx = 84, ry = 31; // just outside drawRing's 72×26 rim
  const N = 3;
  const spin = now * 2.1; // ~0.33 rev/s
  ctx.save();
  for (let i = 0; i < N; i++) {
    const a = spin + (i * 2 * Math.PI) / N;
    const px = cx + rx * Math.cos(a);
    const py = cy + ry * Math.sin(a);
    // point the chevron inward, at the fighter's center
    const ang = Math.atan2(cy - py, cx - px);
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(ang);
    const s = 10;
    ctx.beginPath();
    ctx.moveTo(-s, -s * 0.9);
    ctx.lineTo(s, 0);
    ctx.lineTo(-s, s * 0.9);
    ctx.lineTo(-s * 0.35, 0);
    ctx.closePath();
    // dark outline + solid fill so the chevron stays crisp over any sprite/floor
    ctx.fillStyle = c.fill;
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = 'rgba(0,0,0,0.8)';
    ctx.stroke();
    ctx.restore();
  }
  ctx.restore();
}
